import { Pencil } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../atoms/Icon'
import { Input } from '../atoms/Input'

/**
 * InlineEditField — a label + value row inside a SystemBlockTile that
 * becomes editable on click/hover.
 *
 * Visual language from the Phase 2 mockup `.sys-chip-inline-field`:
 *   - Idle: dark chip background, monospace key + value, faint pencil icon
 *   - Hover: teal dashed border on the value area, pencil glows teal
 *   - Editing: native input with teal focus ring
 *   - Error: red helper text below the row
 *
 * Enter commits; Escape reverts. Blur also commits if the value changed.
 *
 * Composes: Input (atom), Icon (atom).
 */

export interface InlineEditFieldProps {
  /** Short key label shown in the fixed-width left column (e.g. "access"). */
  label: string
  /** Current committed value. */
  value: string
  /** Called when the user commits a changed value (Enter or blur). */
  onChange: (v: string) => void
  /** Placeholder when the input is blank. */
  placeholder?: string
  /** Error text shown below the row. Toggles the error appearance on the input. */
  error?: string
  /** Extra classes for the outer chip. */
  className?: string
}

export const InlineEditField = React.forwardRef<HTMLDivElement, InlineEditFieldProps>(
  ({ label, value, onChange, placeholder = '', error, className }, ref) => {
    const [editing, setEditing] = React.useState(false)
    const [draft, setDraft] = React.useState(value)
    const inputRef = React.useRef<HTMLInputElement | null>(null)

    // Keep draft in sync when the parent pushes a new value while idle.
    React.useEffect(() => {
      if (!editing) setDraft(value)
    }, [value, editing])

    // Auto-focus + select-all when edit mode is entered.
    React.useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus()
        inputRef.current.select()
      }
    }, [editing])

    const enterEdit = React.useCallback(() => setEditing(true), [])

    const commit = React.useCallback(() => {
      const trimmed = draft.trim()
      if (trimmed && trimmed !== value) onChange(trimmed)
      else setDraft(value)
      setEditing(false)
    }, [draft, onChange, value])

    const revert = React.useCallback(() => {
      setDraft(value)
      setEditing(false)
    }, [value])

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          revert()
        }
      },
      [commit, revert],
    )

    const chipBase = cn(
      'flex items-center gap-2 px-[9px] py-[7px]',
      'rounded-md bg-ia-elevated border border-ia-border',
      error && 'border-ia-error/50',
    )

    return (
      <div ref={ref} className={cn('flex flex-col gap-1', className)}>
        {editing ? (
          /* Edit mode: plain div wrapper so the Input has full focus control */
          <div className={chipBase}>
            <span
              aria-hidden="true"
              className="shrink-0 w-[60px] font-mono text-[10px] tracking-[0.06em] text-ia-text-tertiary"
            >
              {label}
            </span>
            <Input
              ref={inputRef}
              size="sm"
              value={draft}
              placeholder={placeholder}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              invalid={!!error}
              aria-label={label}
              autoComplete="off"
              spellCheck={false}
              className="flex-1 min-w-0 font-mono text-[11.5px] h-auto py-[2px] px-[4px]"
            />
          </div>
        ) : (
          /* Idle mode: button so the whole chip is keyboard-activatable */
          <button
            type="button"
            aria-label={`Edit ${label}`}
            onClick={enterEdit}
            className={cn(
              chipBase,
              'group w-full text-left',
              'cursor-text transition-[border-color,background-color] duration-150',
              'hover:border-[rgba(45,212,191,0.30)] hover:bg-[rgba(45,212,191,0.02)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            <span
              aria-hidden="true"
              className="shrink-0 w-[60px] font-mono text-[10px] tracking-[0.06em] text-ia-text-tertiary"
            >
              {label}
            </span>
            <span className="flex-1 min-w-0 font-mono text-[11.5px] text-ia-text truncate">
              {value || <span className="text-ia-text-muted italic">{placeholder}</span>}
            </span>
            <span
              aria-hidden="true"
              className="shrink-0 text-ia-text-quiet group-hover:text-[#2dd4bf] transition-colors duration-150"
            >
              <Icon icon={Pencil} size="xs" />
            </span>
          </button>
        )}

        {/* Error text */}
        {error && (
          <p role="alert" className="text-[11px] text-ia-error leading-snug px-[9px]">
            {error}
          </p>
        )}
      </div>
    )
  },
)
InlineEditField.displayName = 'InlineEditField'
